import DashboardNavigationLocations from "@/core/navigation/DashboardNavigationLocations";
import type { FeaturePermissionKey } from "./featurePermissions";

/**
 * Required feature permission keys per dashboard route (direct URL / navigation).
 * Locations omitted here are not gated at the router (pages may still enforce tuples).
 */

const dashboardTestPermissions: readonly FeaturePermissionKey[] = [
  "CUSTOMERS_LIST_READ",
];

export const DASHBOARD_ROUTE_FEATURE_KEYS: Partial<
  Record<DashboardNavigationLocations, readonly FeaturePermissionKey[]>
> = {
  [DashboardNavigationLocations.COMMERCIAL_DASHBOARD]:
    process.env.NODE_ENV === "development"
      ? dashboardTestPermissions
      : ["DASHBOARD_P1_READ"],
  [DashboardNavigationLocations.AGENCE_DASHBOARD]:
    process.env.NODE_ENV === "development"
      ? dashboardTestPermissions
      : ["DASHBOARD_P2_READ"],
  [DashboardNavigationLocations.COMPLIANCE_DASHBOARD]:
    process.env.NODE_ENV === "development"
      ? dashboardTestPermissions
      : ["DASHBOARD_P3_READ"],
  [DashboardNavigationLocations.CONFORMITE_DASHBOARD]:
    process.env.NODE_ENV === "development"
      ? dashboardTestPermissions
      : ["DASHBOARD_P4_READ"],
  [DashboardNavigationLocations.PROSPECTS]: ["CUSTOMERS_LIST_READ"],
  [DashboardNavigationLocations.CLIENT_360]: ["CUSTOMERS_LIST_READ"],
  [DashboardNavigationLocations.PROFIL]: ["CUSTOMERS_PROFILE_READ"],
  /** Either role catalog or matrix read is enough to open administration (deep link / route). */
  [DashboardNavigationLocations.ROLES_AND_RIGHTS]: [
    "READ_ROLES",
    "READ_PERMISSIONS_MATRIX",
  ],
  [DashboardNavigationLocations.NOUVELLE_DEMANDE]: ["ONBOARDING_CASE_READ"],
  [DashboardNavigationLocations.CREATE_PROSPECT]: ["ONBOARDING_CASE_WRITE"],
  [DashboardNavigationLocations.CREATE_PROSPECT_PP]: ["ONBOARDING_CASE_WRITE"],
  [DashboardNavigationLocations.CREATE_PROSPECT_PM]: ["ONBOARDING_CASE_WRITE"],
  [DashboardNavigationLocations.ONBOARDING]: ["ONBOARDING_CASE_READ"],
  [DashboardNavigationLocations.PM_ONBOARDING]: ["ONBOARDING_CASE_READ"],
  [DashboardNavigationLocations.KYB_CASE_DETAIL]: ["COMPLIANCE_KYB_CASE_READ"],
  [DashboardNavigationLocations.KYB_MANDATES]: ["COMPLIANCE_KYB_CASE_READ"],
  [DashboardNavigationLocations.KYB_CORPORATE_ACCESS]: ["COMPLIANCE_KYB_CORPORATE_ACCESS_READ"],
  [DashboardNavigationLocations.KYB_ACCESS_RECERTIFICATION]: ["COMPLIANCE_KYB_ACCESS_RECERTIFICATION_READ"],
  [DashboardNavigationLocations.KYB_MONITORING]: ["COMPLIANCE_KYB_MONITORING_READ", "COMPLIANCE_KYB_ACCESS_MONITORING_READ"],
  [DashboardNavigationLocations.KYB_RETENTION]: ["COMPLIANCE_KYB_RETENTION_READ"],
  [DashboardNavigationLocations.KYB_PERIMETER]: ["COMPLIANCE_KYB_COUNTRY_SCOPE_READ"],
  [DashboardNavigationLocations.KYB_COMPLEMENT_DOCUMENTS]: ["COMPLIANCE_KYB_DOCUMENT_MATRIX_WRITE"],
  [DashboardNavigationLocations.KYB_SIGNATURE_GRIDS]: ["COMPLIANCE_KYB_CASE_READ"],
  [DashboardNavigationLocations.IDENTITY_REPLACEMENT]: ["ONBOARDING_CASE_READ"],
  [DashboardNavigationLocations.COMPLIANCE_L1_REVIEW]: ["COMPLIANCE_L1_REVIEW_READ"],
  [DashboardNavigationLocations.COMPLIANCE_P4_VALIDATION]: [
    "COMPLIANCE_P4_VALIDATION_READ",
    "COMPLIANCE_P4_VALIDATION_DECIDE",
  ],
  [DashboardNavigationLocations.COMPLIANCE_EDD]: ["COMPLIANCE_EDD_READ"],
  [DashboardNavigationLocations.COMPLIANCE_EDD_DETAIL]: ["COMPLIANCE_EDD_READ"],
};

/**
 * Required raw role names (see hasAnyRoleLabel) per dashboard route, for routes
 * reserved to a persona the domain/action/resource permission catalog does not
 * represent (BOX_AFRICA_ADMIN, same gate as the backend's
 * IdentityPolicyController/ScreeningPolicyController, hasRole not RBAC tuples).
 */
export const DASHBOARD_ROUTE_ROLE_LABELS: Partial<
  Record<DashboardNavigationLocations, readonly string[]>
> = {
  [DashboardNavigationLocations.PLATFORM_SETTINGS]: ["BOX_AFRICA_ADMIN"],
};

const dashboardRouteRoleLabelMap = new Map<
  DashboardNavigationLocations,
  readonly string[]
>(
  Object.entries(DASHBOARD_ROUTE_ROLE_LABELS) as Array<
    [DashboardNavigationLocations, readonly string[]]
  >,
);

export function getDashboardRouteRoleLabels(
  location: DashboardNavigationLocations,
): readonly string[] | null {
  const labels = dashboardRouteRoleLabelMap.get(location);
  return labels?.length ? labels : null;
}

/** Routes that only make sense when the KYB module is active (legal-entity onboarding). */
const KYB_MODULE_GATED_LOCATIONS = new Set<DashboardNavigationLocations>([
  DashboardNavigationLocations.CREATE_PROSPECT_PM,
  DashboardNavigationLocations.PM_ONBOARDING,
  DashboardNavigationLocations.KYB_CASE_DETAIL,
  DashboardNavigationLocations.KYB_MANDATES,
  DashboardNavigationLocations.KYB_CORPORATE_ACCESS,
  DashboardNavigationLocations.KYB_ACCESS_RECERTIFICATION,
  DashboardNavigationLocations.KYB_MONITORING,
  DashboardNavigationLocations.KYB_RETENTION,
  DashboardNavigationLocations.KYB_PERIMETER,
  DashboardNavigationLocations.KYB_COMPLEMENT_DOCUMENTS,
  DashboardNavigationLocations.KYB_SIGNATURE_GRIDS,
]);

export function isKybModuleGatedRoute(
  location: DashboardNavigationLocations,
): boolean {
  return KYB_MODULE_GATED_LOCATIONS.has(location);
}

const dashboardRouteFeatureKeyMap = new Map<
  DashboardNavigationLocations,
  readonly FeaturePermissionKey[]
>(
  Object.entries(DASHBOARD_ROUTE_FEATURE_KEYS) as Array<
    [DashboardNavigationLocations, readonly FeaturePermissionKey[]]
  >,
);

export function getDashboardRouteFeatureKeys(
  location: DashboardNavigationLocations,
): readonly FeaturePermissionKey[] | null {
  const keys = dashboardRouteFeatureKeyMap.get(location);
  return keys?.length ? keys : null;
}
